import PropTypes from 'prop-types';
import React, {Component} from 'react';
import moment from 'moment';

//Utils
import events from '../../../../lib/events.js';
import {dateTimeFormats, salesProductCategory, salesProductCategoryFormat} from 'helpers/constants';

//Components
import ReportDropDown from '../components/reportDropDown.jsx';
import PrintableReport from '../components/printableReport.jsx';
import ReportTable from '../components/reportTable.jsx';
import Loader from '../../../components/loader.jsx';
import Pagination from '../../../components/layout/pagination.jsx';

//Apis
import ReportApi from '../../../../api/report.jsx';

import {FormattedMessage, intlShape, injectIntl} from 'react-intl';

const filterOptions = {
  store: '',
  storeOptions: [],
  productCategory: '',
  productCategoryOptions: [],
  year: moment().year(),
  yearOptions: [],
};

const tableMaxRows = 20;

class ProductsRecommendedReport extends Component {
  static propTypes = {
    branches: PropTypes.any,
    intl: intlShape.isRequired,
  };

  constructor(props) {
    super(props);
    let {formatMessage} = this.props.intl;

    //Setup store filter
    filterOptions.store = '';
    filterOptions.storeOptions = [
      {value: '', label: formatMessage({id: 'general.all.stores'})},
    ];
    for (let i = 0; i < this.props.branchesInfo.length; i++) {
      filterOptions.storeOptions.push({
        value: this.props.branchesInfo[i].id, label: this.props.branchesInfo[i].name,
      });
    }

    //Setup category filter
    filterOptions.productCategoryOptions = [
      {value: '', label: formatMessage({id: 'general.all'})},
    ];
    const categories = salesProductCategoryFormat(formatMessage);

    for (let key in categories) {
      if (categories.hasOwnProperty(key)) {
        filterOptions.productCategoryOptions.push({
          value: key, label: categories[key],
        });
      }
    }

    //Setup year filter
    filterOptions.yearOptions = [
      {value: moment().year(), label: formatMessage({id: 'general.date.ytd'}, {year: moment().year()})},
    ];
    for (let i = moment().year() - 1; i >= 2015; i--) {
      filterOptions.yearOptions.push({value: i, label: i});
    }

    //Set state
    this.state = {
      loaded: true,
      summary: null,
      report: null,
      year: filterOptions.year,
      store: '',
      productCategory: filterOptions.productCategory,
      ...filterOptions,
      count: 0,
      total: 0,
      page: 1,
      showNoDataMessage: false,
    };
  }

  onPageChange = (page, event) => {
    const {count, fullReport} = this.state;
    const pagReport = fullReport.slice((page - 1) * count, count * page);

    this.setState({
      report: pagReport,
      page: page,
    });
  };

  exportReport = () => {
    const {year, store, productCategory} = this.state;

    ReportApi.getProductRecommendationReportExport(
      this.props.intl.locale,
      year,
      store,
      productCategory
    );
  };

  printReport = () => {
    const {fullReport, year} = this.state;
    let {formatMessage} = this.props.intl;
    const columns = [
      {name: formatMessage({id: 'general.product.name'}), key: 'Name'},
      {name: formatMessage({id: 'general.product.category'}), key: 'Category'},
      {name: formatMessage({id: 'general.total.recommended'}), key: 'Quantity'},
      {name: formatMessage({id: 'general.times.recommended'}), key: 'Count'},
    ];

    return <PrintableReport
      report={fullReport}
      columns={columns}
      start={moment(year, 'YYYY').startOf('year').format(dateTimeFormats.numeralDate)}
      startLabel={formatMessage({id: 'general.start.date'})}
      end={moment(year, 'YYYY').endOf('year').format(dateTimeFormats.numeralDate)}
      endLabel={formatMessage({id: 'general.end.date'})}
      title={formatMessage({id: 'reports.products.recommended'})}/>;
  };

  resetFilters = () => {
    this.setState({
      year: moment().year(),
      store: '',
      productCategory: filterOptions.productCategory,
    });
  };

  roundValue = val => parseFloat(Math.round(parseInt(val) * 100) / 100).toFixed(2);

  updateReport = () => {
    const {year, store, productCategory} = this.state;

    this.setState({
      loaded: false,
    });

    const categoryMapping = Object.values(salesProductCategory)
      .reduce((acc, x) => {
        let index = Object.keys(salesProductCategory)
          .find(i => salesProductCategory[i] === x);

        acc[x] = salesProductCategoryFormat(this.props.intl.formatMessage)[index];

        return acc;
      }, {});

    ReportApi.getProductRecommendationReport(
      year,
      store,
      productCategory
    ).then(data => {
      data = data.map(x => {
        x.Category = categoryMapping[x.Category] || x.Category;
        return x;
      });


      this.setState({
        loaded: true,
        fullReport: data,
        report: data.slice(0, tableMaxRows),
        count: tableMaxRows,
        total: data.length,
        page: 1,
        showNoDataMessage: false,
      });
      if (!Object.keys(data).length) {
        this.setState({showNoDataMessage: true});
      }
    }).catch(err => {
      console.log(err);
      this.setState({showNoDataMessage: true});
    }).complete(() => {
      this.setState({
        loaded: true,
      });
    });
  };

  render() {
    const {
      store, storeOptions, productCategory, productCategoryOptions,
      year, yearOptions, loaded, report, showNoDataMessage,
    } = this.state;

    let {formatMessage} = this.props.intl;

    const start = moment(year, 'YYYY').startOf('year');
    const end = moment(year, 'YYYY').endOf('year');


    return <div className='report'>
      <div className='row report-description no-padding'>
        <div className='col-sm-12'><FormattedMessage id="reports.product.recommended.description"/></div>
      </div>
      <div className='row'>
        <div className='col-sm-12'>
          <div className="block-header">
            <FormattedMessage id="general.report.options"/>
          </div>
        </div>
      </div>
      <div className='filter-section'>
        <div className='row'>
          <div className='col-sm-3'>
            <ReportDropDown value={year}
                            label={formatMessage({id: 'general.year'})}
                            onChange={e => {
                              this.setState({year: e.target.value});
                            }}
                            options={yearOptions}/>
          </div>
        </div>

        <div className='row'>
          <div className='col-sm-3'>
            <ReportDropDown value={store}
                            label={formatMessage({id: 'general.store'})}
                            onChange={e => {
                              this.setState({store: e.target.value});
                            }}
                            options={storeOptions}/>
          </div>

          <div className='col-sm-3'>
            <ReportDropDown value={productCategory}
                            label={formatMessage({id: 'general.product.category'})}
                            onChange={e => {
                              this.setState({productCategory: e.target.value});
                            }}
                            options={productCategoryOptions}/>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12">
            <button className='btn btn-primary pull-right'
                    onClick={this.updateReport}><FormattedMessage id="reports.update.summary"/></button>
            <button className='btn text-button pull-right reset-button'
                    onClick={this.resetFilters}><FormattedMessage id="general.reset"/></button>
          </div>
        </div>
      </div>
      <Loader loaded={loaded}>
        {report ?
          <div>
            <div className='row'>
              <div className='col-sm-12 divider-line no-padding-bottom'>
                <h2 className="no-margin-bottom"><FormattedMessage id="reports.report.summary"/>
                  <span className="sub-date">
                      &nbsp;|&nbsp;{end.isBefore(moment()) ?
                        <FormattedMessage id="general.date.range.labelled"
                                      values={{
                                        start: start.format(dateTimeFormats.numeralDate),
                                        end: end.format(dateTimeFormats.numeralDate),
                                      }}/> :
                        <FormattedMessage id='general.present.range.labelled'
                                      values={{
                                        start: start.format(dateTimeFormats.numeralDate),
                                      }}/>}
                  </span>
                </h2>
                <span><FormattedMessage id="reports.reveal.report.summary"/></span>
                {showNoDataMessage ?
                  <label className="report-no-data"><FormattedMessage id="reports.error.no.results"/></label> : ''}
              </div>
            </div>

            {!showNoDataMessage ?
              <div>
                <ReportTable data={report}
                             headers={[
                               {
                                 label: formatMessage({id: 'general.product.name'}), key: 'Name',
                               },
                               {
                                 label: formatMessage({id: 'general.product.category'}), key: 'Category',
                               },
                               {
                                 label: formatMessage({id: 'general.total.recommended'}), key: 'Quantity',
                                 style: {'textAlign': 'right'},
                               },
                               {
                                 label: formatMessage({id: 'general.times.recommended'}), key: 'Count',
                                 style: {'textAlign': 'right'},
                               },
                             ]}/>
                <div className='center-pagination'>
                  <Pagination
                    onPageChange={this.onPageChange}
                    page={this.state.page}
                    count={this.state.count}
                    total={this.state.total}/>
                </div>
              </div> :
              ''}
          </div> : ''
        }

        <div className='col-sm-12 button-group divider-line'>
          <div className='row'>
            <div className='col-sm-12'>
              <button className='btn btn-primary pull-right'
                      onClick={this.exportReport}><FormattedMessage id="reports.export.report"/></button>
              <button className='btn btn-default pull-right'
                      disabled={report === null}
                      onClick={() => {
                        events.publish('print', this.printReport());
                      }}><FormattedMessage id="reports.print.summary"/></button>
            </div>
          </div>
        </div>
      </Loader>
    </div>;
  }
}

export default injectIntl(ProductsRecommendedReport);
